import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/router";
import { IoLogoWhatsapp, IoMdArrowForward } from "react-icons/io";
import { IoCloudUploadOutline } from "react-icons/io5";
import MainLayout from "@/Component/MainLayout";
import Breadcrumbs from "@/Component/Common/Breadcrumbs";
import DownloadNH from "@/Component/Common/DownloadNH";
import FaqSection from "@/Component/Common/FaqSection";
import PageHero from "@/Component/Common/PageHero";
import PhoneInputField from "@/Common/PhoneInputField";
import { submitContactUs } from "@/services/api/common";
import { isIndianUser } from "@/utils/getCountryCode";
import {
  buildContactFormData,
  ENQUIRY_TYPES,
  validateContactForm,
} from "@/utils/contactForm";
import { isEnquirySuccess, saveEnquiryReturnUrl } from "@/utils/enquiryForm";
import { withRegionPath } from "@/utils/session";
import styles from "./ContactUs.module.css";
import { MdEmail, MdLocationOn, MdPhone } from "react-icons/md";

const MAP_EMBED = {
  india:
    "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1108.0420821441483!2d75.86582613650822!3d26.815965337525054!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xa50aece48bb3d187%3A0x2704b3264679c934!2sNext%20Holidays%20-%20Best%20Travel%20Agency%20in%20India!5e1!3m2!1sen!2sin!4v1781606037565!5m2!1sen!2sin",
  uae: "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d57767.808658718226!2d55.260553!3d25.186759!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xcbe788b3563a331%3A0x1d30bb6cd8d67198!2sNext%20Holidays%20-%20Best%20Travel%20Agency%20in%20Dubai%2C%20UAE!5e0!3m2!1sen!2sin!4v1754144401368!5m2!1sen!2sin",
};

const CONTACT_INFO = {
  india: {
    phone: "+91 96100 06398",
    phoneHref: "tel:+919610006398",
    email: "sales.ind@nextholidays.com",
    emailHref: "mailto:sales.ind@nextholidays.com",
    whatsappText: "Start live chat on WhatsApp",
    whatsappHref: "https://wa.me/919610006398",
    address:
      "VI Floor, Scheme, Plot No. 12-A, Shiv Nagar A, Gram, Ramnagariya, Jaipur, Raj 302017",
    directionsHref:
      "https://www.google.com/maps/search/?api=1&query=Next+Holidays+Jaipur",
  },
  uae: {
    phone: "+971 800500777",
    phoneHref: "tel:+971800500777",
    email: "info@nextholidays.com",
    emailHref: "mailto:info@nextholidays.com",
    whatsappText: "Start live chat on WhatsApp",
    whatsappHref: "https://wa.me/971565071346",
    address: "1210-1211, Regal Tower, Business Bay Dubai, UAE",
    directionsHref:
      "https://www.google.com/maps/search/?api=1&query=Next+Holidays+Dubai",
  },
};

const FAQ_ITEMS = [
  {
    id: "cancel-booking",
    question: "How to cancel a booking?",
    answer:
      "To cancel a booking, contact our support team with your booking reference number. Our travel experts will guide you through the cancellation process and inform you of any applicable charges based on the provider's policy.",
  },
  {
    id: "refund-process",
    question: "What is the refund process?",
    answer:
      "Once your cancellation is confirmed, refunds are processed to your original payment method. Processing times may vary depending on your bank or payment provider, typically within 7–14 business days.",
  },
  {
    id: "apply-visa",
    question: "How to apply for a VISA?",
    answer:
      "You can apply for a visa through Next Holidays by selecting your destination and visa type on our website, or by contacting our visa specialists directly. We'll assist with documentation and submission.",
  },
  {
    id: "change-dates",
    question: "How to change travel dates?",
    answer:
      "To change travel dates, reach out to our support team with your booking details. Date changes are subject to availability and any fare or policy differences charged by the service provider.",
  },
];

const INITIAL_FORM = {
  fullName: "",
  email: "",
  phone: undefined,
  enquiryType: "",
  message: "",
};

function ReachCard({ icon, title, detail, linkLabel, href, external }) {
  const linkProps = external
    ? { target: "_blank", rel: "noopener noreferrer" }
    : {};

  return (
    <article className={styles.reachCard}>
      <div className={styles.reachCardDetail}>
        <span className={styles.reachIconWrap} aria-hidden="true">
          {/* <Icon size={20} fontSize={20} /> */}
          {icon}
        </span>
        <h3 className={styles.reachTitle}>{title}</h3>
        <p className={styles.reachDetail}>{detail}</p>
      </div>
      <a href={href} className={styles.reachLink} {...linkProps}>
        {linkLabel} <IoMdArrowForward size={16} />
      </a>
    </article>
  );
}

export default function ContactUs() {
  const router = useRouter();
  const contentRef = useRef(null);
  const formRef = useRef(null);
  const fileInputRef = useRef(null);

  const [isIndia, setIsIndia] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    setIsIndia(isIndianUser());
  }, []);

  const contact = isIndia ? CONTACT_INFO.india : CONTACT_INFO.uae;
  const mapSrc = isIndia ? MAP_EMBED.india : MAP_EMBED.uae;

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const applyFile = (file) => {
    if (!file) return;

    const name = String(file.name || "").toLowerCase();
    const allowed = [".svg", ".png", ".jpg", ".jpeg", ".pdf"].some((ext) =>
      name.endsWith(ext)
    );
    if (!allowed) {
      setErrors((prev) => ({ ...prev, file: "Use SVG, PNG, JPG or PDF." }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, file: "File size should not exceed 5 MB." }));
      return;
    }

    setSelectedFile(file);
    setFileName(file.name);
    if (fileInputRef.current) {
      const transfer = new DataTransfer();
      transfer.items.add(file);
      fileInputRef.current.files = transfer.files;
    }
    setErrors((prev) => {
      if (!prev.file) return prev;
      const next = { ...prev };
      delete next.file;
      return next;
    });
  };

  const handleFileChange = (e) => {
    applyFile(e.target.files?.[0] || null);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    applyFile(e.dataTransfer.files?.[0] || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const file = selectedFile || fileInputRef.current?.files?.[0] || null;
    const nextErrors = validateContactForm(form, file);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const formData = buildContactFormData(form, {
      url: typeof window !== "undefined" ? window.location.href : "",
      file,
    });

    if (!formData) {
      setErrors((prev) => ({ ...prev, phone: "Enter a valid mobile number" }));
      return;
    }

    setLoading(true);
    try {
      const response = await submitContactUs(formData);
      if (!isEnquirySuccess(response)) {
        throw new Error(response?.message || "Submission failed");
      }
      setForm(INITIAL_FORM);
      setFileName("");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      saveEnquiryReturnUrl();
      router.push(withRegionPath("/thank-you"));
    } catch (err) {
      alert(
        err?.response?.data?.message ||
        err?.message ||
        "Failed to send message. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout hideHeader>
      <div className={styles.page}>
        <PageHero
          webImageUrl={isIndia ? "/assets/images/ContactBannerIndia.webp" : "/assets/images/ContactBanner.webp"}
          alt="Contact us banner"
          scrollTargetRef={contentRef}
        />
        <Breadcrumbs />
        <section className={`standard-padding ${styles.reachSection}`} ref={contentRef}>
          {/* <h2 className="standard-heading">Multiple Ways To Reach Us</h2> */}
          <div className={styles.header}>
            <div className={styles.headingBlock}>
              <h2 id="customer-reviews-title" className={"standard-heading"}>
                Get in touch, your way
              </h2>
              <p className="standard-subheading">
                Choose the way that works best for you.
              </p>
            </div>
          </div>
          <div className={styles.reachGrid}>
            <ReachCard
              icon={<MdPhone size={20} />}
              title="Phone Support"
              detail={contact.phone}
              linkLabel="Call Now"
              href={contact.phoneHref}
            />
            <ReachCard
              icon={<MdEmail size={20} />}
              title="Email Support"
              detail={contact.email}
              linkLabel="Send Email"
              href={contact.emailHref}
            />
            <ReachCard
              icon={<IoLogoWhatsapp size={20} />}
              title="WhatsApp Support"
              detail={contact.whatsappText}
              linkLabel="Chat Now"
              href={contact.whatsappHref}
              external
            />
            <ReachCard
              icon={<MdLocationOn size={22} />}
              title="Visit Us"
              detail={contact.address}
              linkLabel="Get Directions"
              href={contact.directionsHref}
              external
            />
          </div>
        </section>

        <section
          ref={formRef}
          id="contact-form"
          className={`standard-padding ${styles.formSection}`}
        >
          <div className={styles.formCard}>
            <div className={styles.mapWrap}>
              <iframe
                title="Next Holidays office location"
                src={mapSrc}
                className={styles.mapFrame}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>

            <div className={styles.formWrap}>
              <h2 className={styles.formTitle}>Send us a message</h2>

              <form className={styles.form} onSubmit={handleSubmit} noValidate>
                <div className={styles.fieldGrid}>
                  <label className={styles.field}>
                    <span className={styles.fieldLabel}>Full Name</span>
                    <input
                      id="contact-full-name"
                      type="text"
                      placeholder="Your Full Name"
                      value={form.fullName}
                      onChange={(e) => updateField("fullName", e.target.value)}
                      className={`${styles.input} ${errors.fullName ? styles.inputError : ""}`}
                    />
                    {errors.fullName ? (
                      <span className={styles.errorText}>{errors.fullName}</span>
                    ) : null}
                  </label>

                  <div className={styles.field}>
                    <span className={styles.fieldLabel}>Phone Number</span>
                    <PhoneInputField
                      id="contact-phone"
                      value={form.phone}
                      onChange={(value) => updateField("phone", value)}
                      error={Boolean(errors.phone)}
                      placeholder="Your Phone Number"
                      className={styles.phoneField}
                    />
                    {errors.phone ? (
                      <span className={styles.errorText}>{errors.phone}</span>
                    ) : null}
                  </div>
                </div>

                <div className={styles.fieldGrid}>
                  <label className={styles.field}>
                    <span className={styles.fieldLabel}>Email ID</span>
                    <input
                      id="contact-email"
                      type="email"
                      placeholder="Your Email ID"
                      value={form.email}
                      onChange={(e) => updateField("email", e.target.value)}
                      className={`${styles.input} ${errors.email ? styles.inputError : ""}`}
                    />
                    {errors.email ? (
                      <span className={styles.errorText}>{errors.email}</span>
                    ) : null}
                  </label>

                  <label className={styles.field}>
                    <span className={styles.fieldLabel}>Enquiry Type</span>
                    <select
                      id="contact-enquiry-type"
                      value={form.enquiryType}
                      onChange={(e) => updateField("enquiryType", e.target.value)}
                      className={`${styles.input} ${styles.select} ${errors.enquiryType ? styles.inputError : ""}`}
                    >
                      <option value="">Select Enquiry Type</option>
                      {ENQUIRY_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    {errors.enquiryType ? (
                      <span className={styles.errorText}>{errors.enquiryType}</span>
                    ) : null}
                  </label>
                </div>

                <label className={styles.field}>
                  <span className={styles.fieldLabel}>Message</span>
                  <textarea
                    id="contact-message"
                    rows={4}
                    placeholder="How can we help you plan your trip?"
                    value={form.message}
                    onChange={(e) => updateField("message", e.target.value)}
                    className={`${styles.input} ${styles.textarea} ${errors.message ? styles.inputError : ""}`}
                  />
                  {errors.message ? (
                    <span className={styles.errorText}>{errors.message}</span>
                  ) : null}
                </label>

                <div className={styles.field}>
                  <span className={styles.fieldLabel}>File Upload (Optional)</span>
                  <button
                    type="button"
                    className={`${styles.uploadBox} ${isDragging ? styles.uploadBoxActive : ""}`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragEnter={handleDragOver}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <IoCloudUploadOutline size={28} aria-hidden="true" />
                    <span className={styles.uploadText}>
                      {fileName || "Click to upload or drag and drop"}
                    </span>
                    <span className={styles.uploadHint}>
                      SVG, PNG, JPG or PDF (max. 5MB)
                    </span>
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".svg,.png,.jpg,.jpeg,.pdf"
                    className={styles.hiddenFileInput}
                    onChange={handleFileChange}
                  />
                  {errors.file ? (
                    <span className={styles.errorText}>{errors.file}</span>
                  ) : null}
                </div>

                <button
                  type="submit"
                  className={styles.submitBtn}
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>
          </div>
        </section>

        <FaqSection
          items={FAQ_ITEMS}
        />

        <DownloadNH />
      </div>
    </MainLayout>
  );
}
